import {
  createFileRoute,
  stripSearchParams,
  type SearchMiddleware,
  type SearchSchemaInput,
} from "@tanstack/react-router";
import { LieferantSeite } from "@/components/LieferantSeite";

/*
 * `/lieferant/$name`. Die einzige Route mit einem Pfad-Parameter.
 *
 * Diese Datei liest den Parameter und reicht ihn als Prop weiter — die
 * Komponente bleibt dadurch ohne Router testbar. TanStack Router liefert den
 * Wert bereits DEKODIERT: aus `/lieferant/Paletten%2FVerpackung` wird hier
 * `Paletten/Verpackung`.
 *
 * Wer von hier aus verlinkt, gibt `params` als Objekt an und lässt den Router
 * kodieren (`<Link to="/lieferant/$name" params={{ name }} />`) — von Hand
 * zusammengebaute Strings verlieren genau die vier Lieferantennamen mit
 * Schrägstrich, die es im Bestand gibt.
 */
/**
 * Die Herkunft, als Search-Param.
 *
 * ═══ Warum diese Route ueberhaupt Search-Params bekommt ══════════════════
 *
 * Weil sie bis zum 25. Durchgang GAR KEINEN Rueckweg hatte. Sie bietet zwei
 * Einstiege VORWAERTS in die Suche und einen Drill-Down hinaus; zurueck zu
 * dem Artikel, von dem man kam, fuehrte nichts ausser dem Zurueck-Knopf des
 * Browsers.
 *
 * Und es gibt genau EINEN Eingang hierher: `ArtikelSeite.tsx`, der
 * Hauptlieferant im Angaben-Block. Die Herkunft ist damit nicht geraten,
 * sondern bekannt — sie steht nur bisher nirgends.
 *
 * ═══ Warum `von` und NICHT der Suchfilter ════════════════════════════════
 *
 * Der naheliegende Weg waere gewesen, den `SucheFilter` durchzureichen wie
 * `/artikel/$id` es tut, und einen „Zurueck zur Suche"-Link zu bauen. Das
 * waere hier der FALSCHE Weg, und zwar gemessen an dieser Seite:
 * `SuchEinstiege` bietet bereits ZWEI Links auf `/suche` an („Alle
 * Positionen in der Suche", „Nur die Slow-Mover"). Ein dritter, der
 * dasselbe Ziel mit einem anderen Filter anspringt, waere nicht ein
 * Rueckweg, sondern ein Ratespiel — drei Links, ein Ziel, drei Filter.
 *
 * `von` fuehrt dorthin zurueck, wo man WAR, und dieses Ziel bietet die Seite
 * sonst nicht an. Das ist der Unterschied zwischen einem Rueckweg und einem
 * zweiten Vorwaertsweg.
 *
 * ═══ Die Adressen-Wiederaufbau-Falle ═════════════════════════════════════
 *
 * Mit einem `validateSearch` faellt diese Route unter denselben Mechanismus
 * wie `/suche`, `/verkaufssuche` und `/artikel/$id`: der Router baut die
 * Adresse beim Mount neu (`_includeValidateSearch: true`) und setzt die
 * weggelassenen Felder wieder ein. Ohne Middleware stuende ab sofort `?von=`
 * an JEDER Lieferanten-Adresse — auch an der, die jemand am Telefon
 * vorliest. Das ist Posten 62, und die Antwort darauf ist dieselbe wie dort.
 *
 * `stripSearchParams` wirft weg, was mit dem Vorgabewert GLEICH ist
 * (`deepEqual`), also genau das leere `von`. Ein gesetztes bleibt stehen.
 * Der Fall ist in `LieferantSeite.test.tsx` gepinnt.
 *
 * Kein `OHNE_LEERE_EINZELWERTE` aus `lib/suche-filter.ts`: das ist eine
 * Middleware ueber `SucheFilter` und kennt dessen 19 Felder. Hier gibt es
 * genau eines, und es ist keines davon.
 */
/** Die Search-Params dieser Route. */
export interface LieferantSuche {
  /**
   * Die Dokument-ID des Artikels, von dem man kam — `""` heisst „unbekannt".
   *
   * Ein STRING und kein Objekt: der Wert geht ungeprueft in einen
   * `/artikel/$id`-Link. Eine von Hand erfundene ID landet damit auf der
   * 404-Auskunft dieser Route, und die ist eine ANTWORT und keine Stoerung
   * (`ArtikelUnbekannt`). Eine Pruefung hier waere eine zweite Wahrheit
   * neben dem Server.
   */
  von: string;
}

const OHNE_LEERE_HERKUNFT: SearchMiddleware<LieferantSuche> = stripSearchParams({ von: "" });

export const Route = createFileRoute("/lieferant/$name")({
  component: LieferantRoute,
  validateSearch: (roh: Record<string, unknown> & SearchSchemaInput): LieferantSuche => ({
    // Mehrfach angegeben gewinnt das ERSTE Vorkommen — dieselbe Regel wie
    // `alsSchalter` in `lib/suche-filter.ts` und wie `einzel()` serverseitig.
    von: typeof roh.von === "string" ? roh.von : (Array.isArray(roh.von) && typeof roh.von[0] === "string" ? roh.von[0] : ""),
  }),
  search: { middlewares: [OHNE_LEERE_HERKUNFT] },
});

function LieferantRoute() {
  const { name } = Route.useParams();
  /*
   * Die Herkunft kommt als PROP herein, nicht ueber einen Hook in der Sicht
   * — dieselbe Trennung, die diese Datei schon fuer `name` zieht, und aus
   * demselben Grund: ein `useSearch()` in `LieferantSeite` zoege den Router
   * in jeden ihrer Tests, und zwar mit einer Route, die es dort nicht gibt.
   */
  const { von } = Route.useSearch();
  return <LieferantSeite name={name} herkunft={von} />;
}
